import { useCallback, useState } from "react";
import { usePaymentForm } from "@apac/components/checkout";
import {
  postSpreedlyPaymentMethod,
  type SpreedlyCreatePaymentMethodRequest,
  type SpreedlyCreditCard,
} from "@hopper-b2b/spreedly-js";
import type { TokenizeCardErrors } from "@hopper-b2b/types";
import type { ICheckoutPaymentFormProps } from "@hopper-b2b/ui";
import { useTranslatedSpreedlyError } from "../../../PaymentInformation/useTranslatedError";

const spreedlyCardFieldToFormFields = {
  year: "expiryDate",
  month: "expiryDate",
  verification_value: "cvv",
  number: "cardNumber",
  first_name: "nameOnCard",
  last_name: "nameOnCard",
  full_name: "nameOnCard",
  zip: "zipCode",
};

type UseAddPaymentParams = Pick<
  ICheckoutPaymentFormProps,
  "onSubmit" | "onError"
> & {
  yearDigit?: "two" | "four";
};

export const useAddPayment = ({
  onSubmit,
  onError,
  yearDigit = "two",
}: UseAddPaymentParams) => {
  const { formContext } = usePaymentForm(yearDigit);
  const { getTranslatedSpreedlyError } = useTranslatedSpreedlyError();

  const [loading, setLoading] = useState(false);

  const getPaymentData = useCallback((): SpreedlyCreatePaymentMethodRequest => {
    const { cardNumber, nameOnCard, expiryDate, cvv, zipCode, country } =
      formContext.getValues();

    const fullName = nameOnCard.trim();
    const firstSpaceIndex = fullName.indexOf(" ");
    const firstName = fullName.substring(0, firstSpaceIndex).trim();
    const otherNames = fullName.substring(firstSpaceIndex + 1).trim();

    const dateParts = expiryDate.split("/");
    const month = dateParts && dateParts.length > 0 ? dateParts[0] : "";
    const year = dateParts && dateParts.length > 1 ? dateParts[1] : "";
    const formattedYear = year.length === 2 ? `20${year}` : year;

    return {
      payment_method: {
        credit_card: {
          first_name: firstName,
          last_name: otherNames,
          // Spreedly parses full_name into first_name/last_name, which lets
          // single-word names (no first name) pass validation
          full_name: fullName,
          number: cardNumber.replace(/\s/g, ""),
          verification_value: cvv,
          month,
          year: formattedYear,
          // need to pass empty string currently, if not provided
          zip: zipCode,
          country,
        } as SpreedlyCreditCard,
      },
    };
  }, [formContext]);

  const onSuccess = useCallback(
    (token: string, last4: string) => {
      onSubmit(token, last4);
    },
    [onSubmit]
  );

  const onFieldErrors = useCallback(
    (errors: Array<TokenizeCardErrors>) => {
      onError(errors);

      errors.forEach((error) => {
        const formField = spreedlyCardFieldToFormFields[error.attribute];

        // Only set error if we have a valid form field mapping
        if (formField) {
          formContext.setError(formField, {
            type: "manual",
            message: getTranslatedSpreedlyError(error) ?? error.message,
          });
        } else {
          // Log unmapped error attributes to help identify missing mappings
          console.warn(
            `Spreedly error attribute '${error.attribute}' is not mapped to a form field`,
            error
          );
        }
      });
    },
    [onError, formContext, getTranslatedSpreedlyError]
  );

  const submitPaymentForm = useCallback(() => {
    if (loading) return;

    // Clear only the fields that could have manual errors from Spreedly validation
    // This preserves React Hook Form's built-in validation (required fields, patterns, etc.)
    formContext.clearErrors([
      "expiryDate",
      "cvv",
      "cardNumber",
      "nameOnCard",
      "zipCode",
    ]);

    setLoading(true);

    const paymentData = getPaymentData();

    postSpreedlyPaymentMethod({
      key: import.meta.env.VITE_SPREEDLY_ENVIRONMENT_KEY,
      data: paymentData,
      onSuccess,
      onError,
      onFieldErrors,
    }).finally(() => {
      setLoading(false);
    });
  }, [loading, formContext, getPaymentData, onSuccess, onError, onFieldErrors]);

  return {
    context: { formContext, addPaymentLoading: loading },
    handlers: { onSubmitForm: formContext.handleSubmit(submitPaymentForm) },
  };
};
